/**
 * PublicNavBar — shared navigation bar for all public-facing sub-pages.
 *
 * Renders on: /blog, /blog/:slug, /how-we-help-writers, /faq, /bookstore
 * and their locale-prefixed equivalents (e.g. /fr/blog, /de/faq).
 *
 * Locale behaviour
 * ─────────────────
 * • Detects an active locale by matching the first path segment against
 *   SUPPORTED_LOCALES.  When found, every nav link gets that prefix so
 *   the user stays in their locale as they browse between sub-pages.
 * • isActive strips the locale prefix before comparing, so /fr/blog
 *   correctly highlights the "Blog" tab.
 * • CTA href callbacks are locale-prefixed too (/fr/signup).
 *
 * Usage:
 *   <PublicNavBar onSignIn={fn} onSignUp={fn} />
 *   // fn receives no args; hook callers build their own handler
 */
import React, { useState, useEffect, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { BookOpen, Menu, X, Smartphone } from 'lucide-react';
import LanguageCapsule from '@/components/LanguageCapsule';
import { ThemeToggle } from '@/components/ThemeToggle';
import { InstallGuideSheet, detectPlatform } from '@/components/PwaInstallCard';
import { triggerReaderInstall } from '@/lib/readerPwaInstall';

const SUPPORTED_LOCALES = new Set(['fr', 'de', 'es', 'pt', 'zh']);

const NAV_LINKS = [
  { label: 'Home',        path: '/',                    testId: 'pub-nav-home' },
  { label: 'Bookstore',   path: '/bookstore',           testId: 'pub-nav-bookstore' },
  { label: 'Blog',        path: '/blog',                testId: 'pub-nav-blog' },
  { label: 'Compare',     path: '/compare',             testId: 'pub-nav-compare' },
  { label: 'How We Help', path: '/how-we-help-writers', testId: 'pub-nav-how-we-help' },
  { label: 'Pricing',     path: '/pricing',             testId: 'pub-nav-pricing' },
  { label: 'FAQ',         path: '/faq',                 testId: 'pub-nav-faq' },
];

export default function PublicNavBar({ onSignIn, onSignUp }) {
  const location = useLocation();
  const [mobileOpen,    setMobileOpen]    = useState(false);
  const [scrolled,      setScrolled]      = useState(false);
  const [showInstGuide, setShowInstGuide] = useState(false);
  const [platform,      setPlatform]      = useState({});

  // ── PWA install prompt capture ────────────────────────────────────────────
  useEffect(() => {
    setPlatform(detectPlatform());
    // Note: we do NOT capture beforeinstallprompt here.  On main-site pages
    // the root manifest (scope "/") would trigger the Writer App install, not
    // the Reader App.  triggerReaderInstall() routes Chrome users to /reader/
    // where the correct Reader PWA prompt fires automatically.
  }, []);

  const handleInstallClick = () => {
    triggerReaderInstall(platform, () => setShowInstGuide(true));
  };

  // ── Locale detection ─────────────────────────────────────────────────────
  // Extract the two-letter locale segment from the first path segment, e.g.
  // /fr/blog → "fr", /blog → "" (empty = English, no prefix).
  const activeLocale = useMemo(() => {
    const match = location.pathname.match(/^\/([a-z]{2})(\/|$)/);
    return match && SUPPORTED_LOCALES.has(match[1]) ? match[1] : '';
  }, [location.pathname]);

  // Prefix for all generated links in the nav.
  const pfx = activeLocale ? `/${activeLocale}` : '';

  // ── Collapse mobile menu on navigation ───────────────────────────────────
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  // ── Thin drop-shadow on scroll ────────────────────────────────────────────
  useEffect(() => {
    const handle = () => setScrolled(window.scrollY > 4);
    window.addEventListener('scroll', handle, { passive: true });
    return () => window.removeEventListener('scroll', handle);
  }, []);

  // ── Active-state detection ────────────────────────────────────────────────
  // Strip any locale prefix before testing so /fr/blog matches path /blog.
  const isActive = (path) => {
    const bare = location.pathname.replace(/^\/[a-z]{2}(\/|$)/, '/');
    if (path === '/') return bare === '/';
    return bare.startsWith(path);
  };

  return (
    <>
    <header
      data-testid="public-navbar"
      className={`sticky top-0 z-50 w-full border-b border-neutral-200 bg-white/95 backdrop-blur-sm transition-shadow ${
        scrolled ? 'shadow-sm' : ''
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-3 sm:px-6">
        {/* Brand */}
        <Link
          to={pfx + '/'}
          data-testid="pub-nav-brand"
          className="flex shrink-0 items-center gap-2 text-neutral-900 no-underline"
        >
          <span className="flex h-7 w-7 items-center justify-center rounded-sm bg-[#F7A04B] text-[#2B2321] shadow-sm">
            <BookOpen className="h-4 w-4" strokeWidth={2} />
          </span>
          <span
            className="text-[15px] font-semibold tracking-tight"
            style={{ fontFamily: 'Fraunces, Georgia, serif' }}
          >
            Crown &amp; Rose
          </span>
        </Link>

        {/* Desktop tabs */}
        <nav
          className="hidden items-center gap-0.5 md:flex"
          data-testid="pub-nav-tabs"
          aria-label="Site navigation"
        >
          {NAV_LINKS.map(({ label, path, testId }) => (
            <Link
              key={path}
              to={pfx + path}
              data-testid={testId}
              aria-current={isActive(path) ? 'page' : undefined}
              className={`rounded-md px-3 py-1.5 text-[13px] font-medium no-underline transition-colors ${
                isActive(path)
                  ? 'bg-[#FDE7D3] text-[#E57A1C]'
                  : 'text-neutral-600 hover:bg-neutral-100 hover:text-neutral-900'
              }`}
            >
              {label}
            </Link>
          ))}

          {/* Sign In — sits between FAQ and Install Reader App */}
          {onSignIn && (
            <button
              type="button"
              data-testid="pub-nav-signin"
              onClick={onSignIn}
              className="ml-1.5 rounded-md border border-neutral-300 px-3 py-1 text-[13px] font-medium text-neutral-700 transition-colors hover:border-neutral-400 hover:bg-neutral-50"
            >
              Sign in
            </button>
          )}

          {/* Install Reader App — right after Sign In */}
          <button
            type="button"
            onClick={handleInstallClick}
            data-testid="pub-nav-install-reader"
            className="ml-1 inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[12px] font-semibold transition-colors hover:bg-[#E57A1C] hover:text-white hover:border-[#E57A1C]"
            style={{ borderColor: '#E57A1C', color: '#E57A1C' }}
          >
            <Smartphone className="h-3 w-3" />
            Install Reader App
          </button>
          {activeLocale && (
            <span
              className="ml-1 rounded-sm border border-neutral-200 bg-neutral-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-neutral-500"
              data-testid="pub-nav-locale-badge"
              title={`Browsing in locale: ${activeLocale}`}
            >
              {activeLocale}
            </span>
          )}
        </nav>

        {/* CTA buttons + mobile hamburger */}
        <div className="flex shrink-0 items-center gap-2">
          {onSignUp && (
            <button
              type="button"
              data-testid="pub-nav-start-trial"
              onClick={onSignUp}
              className="inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-[13px] font-semibold transition-colors hover:bg-[#E57A1C] hover:text-white hover:border-[#E57A1C]"
              style={{ borderColor: '#E57A1C', color: '#E57A1C' }}
            >
              Start free trial
            </button>
          )}
          {/* Language selector — compact Globe + native-label dropdown */}
          <LanguageCapsule
            variant="compact"
            className="hidden text-neutral-600 sm:inline-flex"
            data-testid="pub-nav-language"
          />
          {/* Theme toggle */}
          <ThemeToggle className="hidden sm:flex" />
          {/* Mobile hamburger */}
          <button
            type="button"
            data-testid="pub-nav-menu-toggle"
            onClick={() => setMobileOpen((v) => !v)}
            className="rounded-md p-1.5 text-neutral-600 hover:bg-neutral-100 md:hidden"
            aria-label={mobileOpen ? 'Close navigation' : 'Open navigation'}
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      {mobileOpen && (
        <nav
          data-testid="pub-nav-mobile-menu"
          className="border-t border-neutral-200 bg-white px-4 py-3 md:hidden"
        >
          <ul className="flex flex-col gap-1">
            {NAV_LINKS.map(({ label, path, testId }) => (
              <li key={path}>
                <Link
                  to={pfx + path}
                  data-testid={`${testId}-mobile`}
                  className={`block rounded-md px-3 py-2 text-[14px] font-medium no-underline transition-colors ${
                    isActive(path)
                      ? 'bg-rose-50 text-rose-900'
                      : 'text-neutral-700 hover:bg-neutral-100'
                  }`}
                >
                  {label}
                </Link>
              </li>
            ))}
            <li className="mt-2 flex gap-2 border-t border-neutral-100 pt-2">
              {onSignIn && (
                <button
                  type="button"
                  data-testid="pub-nav-signin-mobile"
                  onClick={onSignIn}
                  className="flex-1 rounded-md border border-neutral-300 py-2 text-[13px] font-medium text-neutral-700"
                >
                  Sign in
                </button>
              )}
              {onSignUp && (
                <button
                  type="button"
                  data-testid="pub-nav-start-trial-mobile"
                  onClick={onSignUp}
                  className="flex-1 rounded-full bg-[#F7A04B] py-2 text-[13px] font-semibold text-[#2B2321]"
                >
                  Start free trial
                </button>
              )}
            </li>
            <li className="border-t border-neutral-100 pt-2">
              <button
                type="button"
                onClick={() => { setMobileOpen(false); handleInstallClick(); }}
                data-testid="pub-nav-install-reader-mobile"
                className="flex w-full items-center justify-center gap-2 rounded-full border py-2 text-[13px] font-semibold transition-colors hover:bg-[#E57A1C] hover:text-white hover:border-[#E57A1C]"
                style={{ borderColor: '#E57A1C', color: '#E57A1C' }}
              >
                <Smartphone className="h-4 w-4" />
                Install Reader App
              </button>
            </li>
          </ul>
        </nav>
      )}
    </header>

    {/* PWA Install Guide — opened from the nav Install button */}
    <InstallGuideSheet
      open={showInstGuide}
      onClose={() => setShowInstGuide(false)}
      platform={platform}
    />
    </>
  );
}
